import type { Parser } from "../../tastyworks/deser"
import {
  EVENT_CONTRACT_CHAIN_DESER,
  EVENT_CONTRACT_CHAIN_LIST_DESER,
  EventContractChain,
  EventContractChainList,
} from "../../tastyworks/event-contract-chain"
import {
  EVENT_CONTRACT_INSTRUMENT_DESER,
  EventContractInstrument,
} from "../../tastyworks/event-contract-instrument"
import {
  EVENT_PRODUCT_DESER,
  EventProduct,
} from "../../tastyworks/event-product"
import {
  parseFuturesContract,
  type FuturesContract,
} from "../../tastyworks/futures-contract"
import type HttpClient from "../../tastyworks/http"
import type { PaginationParams, Params } from "../../tastyworks/request"
import {
  parseItemsResponse,
  parseSingleResponse,
  type ItemsResponse,
  type SingleResponse,
} from "../../tastyworks/response"
import { fetchAllPages } from "../../tastyworks/util/pagination-request"

export interface InstrumentTypeGroup {
  "instrument-type": string
  symbols: string[]
}

export interface EventProductsParams extends Params {
  exchange: string
  frequency?: string
}

function encodeSymbol(symbol: string): string {
  return encodeURIComponent(symbol).replace(/\./g, "%2E")
}

const INSTRUMENTS_API_VERSION = "20260415"
export const INSTRUMENTS_API_HEADERS: HeadersInit = {
  "Accept-Version": INSTRUMENTS_API_VERSION,
}

// NOTE: coincides with instruments-api max page size to utilize caching
const INSTRUMENTS_PER_PAGE_SIZE = 1000

export class InstrumentsService {
  constructor(private readonly instrumentsHttpClient: HttpClient) {}

  readonly getEventProducts = async (
    params: EventProductsParams
  ): Promise<ItemsResponse<EventProduct>> =>
    this.instrumentsHttpClient.indexSimple(
      EVENT_PRODUCT_DESER.toParser(EventProduct),
      "event-products",
      params,
      INSTRUMENTS_API_HEADERS
    )

  readonly getEventContractChain = async (
    symbol: string
  ): Promise<SingleResponse<EventContractChain>> => {
    const helper = await this.instrumentsHttpClient.get(
      `event-contract-chains/${symbol}/nested`,
      undefined,
      INSTRUMENTS_API_HEADERS
    )
    return parseSingleResponse(
      helper,
      EVENT_CONTRACT_CHAIN_DESER.toParser(EventContractChain)
    )
  }

  readonly getEventContractChains = async (
    exchange: string,
    underlyingProduct: string
  ): Promise<SingleResponse<EventContractChainList>> => {
    const helper = await this.instrumentsHttpClient.get(
      `event-contract-chains/${exchange}`,
      { "underlying-product": underlyingProduct },
      INSTRUMENTS_API_HEADERS
    )
    return parseSingleResponse(
      helper,
      EVENT_CONTRACT_CHAIN_LIST_DESER.toParser(EventContractChainList)
    )
  }

  readonly getEventContractChainsByProduct = async (
    exchange: string,
    eventProductExternalId: string
  ): Promise<SingleResponse<EventContractChainList>> => {
    const helper = await this.instrumentsHttpClient.get(
      `event-contract-chains/${exchange}/${encodeURIComponent(eventProductExternalId)}`,
      undefined,
      INSTRUMENTS_API_HEADERS
    )
    return parseSingleResponse(
      helper,
      EVENT_CONTRACT_CHAIN_LIST_DESER.toParser(EventContractChainList)
    )
  }

  readonly show = async (
    symbol: string
  ): Promise<SingleResponse<EventContractInstrument>> =>
    this.instrumentsHttpClient.show(
      new EventContractInstrument(),
      EVENT_CONTRACT_INSTRUMENT_DESER.update,
      `event-contracts/${encodeSymbol(symbol)}`,
      undefined,
      INSTRUMENTS_API_HEADERS
    )

  readonly fetchFuturesContracts = async (): Promise<FuturesContract[]> =>
    fetchAllPages(
      async (params?: PaginationParams) =>
        this.instrumentsHttpClient.indexSimple(
          parseFuturesContract,
          "futures",
          params,
          INSTRUMENTS_API_HEADERS
        ),
      { perPage: INSTRUMENTS_PER_PAGE_SIZE }
    )

  readonly search = async <T>(
    groups: InstrumentTypeGroup[],
    parser: Parser<T>,
    options?: { fields?: Record<string, string> }
  ): Promise<ItemsResponse<T>> => {
    const totalSymbols = groups.reduce(
      (sum, group) => sum + group.symbols.length,
      0
    )
    const helper = await this.instrumentsHttpClient.postJson("search", {
      "page-offset": 0,
      "per-page": totalSymbols,
      "instrument-type-groups": groups,
      ...(options?.fields && { fields: options.fields }),
    })
    const unwrappedParser: Parser<T> = (itemHelper) => {
      const dataHelper = itemHelper.getChild("data")
      return parser(dataHelper)
    }
    return parseItemsResponse(helper, unwrappedParser)
  }
}
